import { ApiFiles, EnumEstadoFichero, EnumGrupoDocumento } from '../../../../api'
import {
  apiFetchDocumentsByCustomFilter,
  apiFetchDocumentsByIdList
} from '../../../../api/Argentia/endpoints/documents/documents.api'
import { DocumentModel } from '../../../../api/Argentia/endpoints/documents/model/document'
import { apiFilesUpload } from '../../../../api/Pioneer/endpoints/files/files.api'
import { FilesLoadingResponse } from '../../../../api/Pioneer/model/Files'
import { EnumTipoDocumento } from '../../../../api/oData'
import { enumKeyToValue } from '../../../../helpers'
import { fileToBase64 } from '../../../../helpers/fileToBase64'
import {
  createErrorServiceResponse,
  createSuccessServiceResponse
} from '../../../../helpers/serviceResponseHelper'
import { deleteFile } from '../../../../helpers/storageHelper'
import i18n from '../../../../i18n'
import { ActionIcons, DataTableActionConfiguration, DatePeriod } from '../../../../model'
import { ServiceResponse } from '../../../../model/serviceResponse'
import { getActiveBalanceZone, getActiveCompany, getCustomerDomainPrefix } from '../../../../redux'
import {
  communicationFileLoadingItemsMapper,
  communicationUploadFileResultItemsMapper,
  comunicationsFilesListItemsMapper
} from './comunicationFiles.mapper'
import { FileModel, UploadedFileResume, UploadedFileStates } from './comunicationFiles.model'

const apiFiles = new ApiFiles()

export const fetchFilesListItems = async (
  datePeriod: DatePeriod
): Promise<ServiceResponse<any>> => {
  const types = [
    EnumGrupoDocumento.SPLG,
    EnumGrupoDocumento.ProvisionInformacion,
    EnumGrupoDocumento.DescargaPlantillas
  ]

  return await apiFetchDocumentsByCustomFilter(types, datePeriod, getActiveCompany())
    .then(res => {
      return createSuccessServiceResponse(comunicationsFilesListItemsMapper(res))
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const checkFilesState = async (
  filesIds: any,
  desiredStates: EnumEstadoFichero[]
): Promise<any> => {
  return await apiFetchDocumentsByIdList(filesIds).then(response => {
    let updatedFiles: DocumentModel[] = []
    response.forEach(op => {
      if (desiredStates.some(x => x === op.state)) {
        updatedFiles.push(op)
      }
    })
    return comunicationsFilesListItemsMapper(updatedFiles)
  })
}

export const notifyToProcessFiles = async (
  files: UploadedFileResume[]
): Promise<FilesLoadingResponse[]> => {
  return apiFiles.FilesLoading(
    files.map(x => x.fileName),
    getActiveCompany(),
    getActiveBalanceZone()
  )
}

export const deleteUnsupportedUploadedFiles = async (files: string[]): Promise<void> => {
  files &&
    files.length > 0 &&
    files.forEach(file => {
      deleteFile(`hd-${getCustomerDomainPrefix()}`, `files/loading-new/${file}`)
    })
}

export const uploadCommunicationFiles = async (
  files: any[],
  documentType?: EnumTipoDocumento
): Promise<ServiceResponse<any>> => {
  const assets = await Promise.all(
    files.map(async f => ({ blobName: f.name, contenido: await fileToBase64(f.file) }))
  )
  return apiFilesUpload({
    files: assets,
    companyId: getActiveCompany()?.id ?? 0,
    balanceZoneId: getActiveBalanceZone()?.id ?? 0,
    documentType,
    renameOnDuplicate: true
  })
    .then(async (response: FilesLoadingResponse[]) => {
      const uploadedFilesResumes = communicationUploadFileResultItemsMapper(response)
      return createSuccessServiceResponse(uploadedFilesResumes)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

//#region Files global actions

export const filterApplicableActionsConfiguration = (
  item: FileModel,
  configurations: Array<DataTableActionConfiguration>
): Array<DataTableActionConfiguration> => {
  const applicableActionsConfiguration: Array<DataTableActionConfiguration> = configurations.filter(
    config => {
      switch (config.icon) {
        case ActionIcons.LOADING: {
          return (
            enumKeyToValue(EnumEstadoFichero, item.state) ===
              enumKeyToValue(EnumEstadoFichero, EnumEstadoFichero.EnProceso) &&
            enumKeyToValue(EnumEstadoFichero, item.state) !==
              enumKeyToValue(EnumEstadoFichero, EnumEstadoFichero.Esperando)
          )
        }
        case ActionIcons.ERROR: {
          return (
            enumKeyToValue(EnumEstadoFichero, item.state) ===
            enumKeyToValue(EnumEstadoFichero, EnumEstadoFichero.Error)
          )
        }
        case ActionIcons.WARNING: {
          return (
            enumKeyToValue(EnumEstadoFichero, item.state) ===
            enumKeyToValue(EnumEstadoFichero, EnumEstadoFichero.Aviso)
          )
        }
        default:
          return false
      }
    }
  )

  return applicableActionsConfiguration
}

export const getGlobalActionsConfiguration = (): Array<DataTableActionConfiguration> => {
  return [getStateLoadingAction()]
}

export const getGlobalObjectionsActionsConfiguration = (): Array<DataTableActionConfiguration> => {
  return [getObjectionsErrorAction(), getObjectionsWarningAction()]
}

const getStateLoadingAction = (): DataTableActionConfiguration => {
  return {
    icon: ActionIcons.LOADING,
    tooltip: i18n.t('comunicationSends:dataTable.tooltips.loading'),
    isLoading: true
  }
}

const getObjectionsErrorAction = (): DataTableActionConfiguration => {
  return {
    icon: ActionIcons.ERROR,
    tooltip: i18n.t('comunicationFiles:dataTable.tooltips.objectionsError')
  }
}

const getObjectionsWarningAction = (): DataTableActionConfiguration => {
  return {
    icon: ActionIcons.WARNING,
    tooltip: i18n.t('comunicationFiles:dataTable.tooltips.objectionsWarning')
  }
}
//#endregion
